<script setup>
import getAuthErrorMessage from "@/Support/getAuthErrorMessage";
import getQuery from "@/Support/getQuery";
import { Link, router, useForm } from "@inertiajs/vue3";
import { onMounted } from "vue";

import MicrosoftButton from "@/Components/Auth/MicrosoftButton.vue";
import LoopLogo from "@/Components/Logos/LoopLogo.vue";
import RSSBxLoopLogin from "@/Components/Logos/RSSBxLoopLogin.vue";

import AuthenticationLayout from "@/Layouts/AuthenticationLayout.vue";

defineProps({
    canResetPassword: Boolean,
    status: {
        type: String,
        default: "",
    },
    isRsvt: {
        type: Boolean,
        default: false,
    },
});

const form = useForm({
    email: "",
    password: "",
    remember: false,
});

onMounted(() => {
    const { sso_auth_token: ssoToken, sso_error: errorCode } = getQuery([
        "sso_auth_token",
        "sso_error",
    ]);

    if (errorCode) {
        form.setError("email", getAuthErrorMessage(errorCode));
        return;
    }

    let formData = null;
    if (ssoToken) {
        formData = {
            token: ssoToken,
            confirmSso: false,
            remember: form.remember ? "on" : "",
        };
    } else {
        return;
    }

    form.transform(() => formData).post(route("login"));
});

const setRequiredErrorMessages = () => {
    const getRequiredMessage = (field) => `The ${field} field is required.`;
    if (!form.email) {
        form.setError("email", getRequiredMessage("email"));
    }
    if (!form.password) {
        form.setError("password", getRequiredMessage("password"));
    }
};

const submit = () => {
    if (!form.email || !form.password) {
        return setRequiredErrorMessages();
    }

    form.transform((data) => ({
        ...data,
        remember: form.remember ? "on" : "",
    })).post(route("login"), {
        onFinish: () => form.reset("password"),
    });
};

const loginWithMicrosoft = () => {
    router.visit(route("login-with-microsoft"));
};
</script>

<template>
    <AuthenticationLayout title="Log in" :is-rsvt="isRsvt">
        <template #alert>
            <Alert
                v-if="status"
                :message="status"
                type="primary-green"
                custom-classes="text-primary"
            />
        </template>
        <RSSBxLoopLogin v-if="isRsvt" class="mx-auto w-2/3 md:w-1/2" />
        <LoopLogo v-else logo-type="login" />

        <div class="w-full">
            <form class="pt-8" @submit.prevent="submit">
                <InputFields
                    id="email"
                    v-model="form.email"
                    placeholder="Email"
                    class="pb-2"
                    :error="form.errors.email"
                />
                <InputFields
                    id="password"
                    v-model="form.password"
                    placeholder="Password"
                    :input-type="'password'"
                    class="pb-2"
                    :error="form.errors.password"
                    @keyup.enter="submit"
                />

                <div class="flex flex-col space-y-2">
                    <GroupButton
                        type="base-light-blue"
                        class="w-full"
                        text="Login"
                        custom-classes="!rounded-lg !py-1 !font-medium !text-primary"
                        solid
                        @click="submit"
                    />
                    <Link
                        v-if="canResetPassword"
                        :href="route('password.request')"
                        class="text-sm font-bold text-base-light-blue underline"
                    >
                        Forgotten password?
                    </Link>

                    <div
                        class="flex justify-center text-sm leading-normal text-white sm:text-lg"
                    >
                        OR
                    </div>

                    <MicrosoftButton
                        class="gap-1 !text-login-page-text shadow-md"
                        custom-classes="!rounded-lg !py-1"
                        solid
                        @click="loginWithMicrosoft()"
                    />
                </div>
            </form>
        </div>
    </AuthenticationLayout>
</template>

<style>
input::placeholder {
    @apply font-normal text-primary;
}
</style>
